import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { MuiFormSelect } from 'components/Form';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';
import { AVATAR_STYLES, getConsistentAvatarBackgroundColor } from 'helpers/avatar';
import { getInitials } from 'helpers/string';
import { DangerousIcon } from 'icons';

import type { ShareTicketFormValuesT } from '../ShareTicketPopup/declarations';
import validationSchema from '../ShareTicketPopup/validationSchema';

import useShareTicketPopup from './hooks/useShareTicketPopup';
import { getPopupContentStyle } from './ShareTicketPopup.styled';

type ShareTicketPopupPropsT = {
  ticketId?: string;
  isOpen?: boolean;
  onClose?: NoopT;
};

const ShareTicketPopup: FC<ShareTicketPopupPropsT> = ({
  ticketId,
  isOpen,
  onClose,
}): JSX.Element => {
  const {
    isLoadingAgentOptions,
    agentIdsOptions,
    handleCloseShareTicketPopup,
    handleSubmitForm,
    isPopupOpened,
    isSharingTicket,
    handleAgentChange,
  } = useShareTicketPopup();
  const { selectedTicketsIds, setSelectedTicketsIds, selectedTicketId } = useTickets();

  const { isMobile } = useResponsive();
  const fromMethods = useForm<ShareTicketFormValuesT>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { handleSubmit, setValue, reset, formState } = fromMethods;

  const isModalOpen = isPopupOpened || !!isOpen;

  useEffect((): void => {
    if (isModalOpen && agentIdsOptions.length === 1) {
      const agentId = agentIdsOptions[0].value as string;
      setValue('shareWithUserId', agentId);
      handleAgentChange(agentId);
    }
  }, [agentIdsOptions, handleAgentChange, isModalOpen, setValue]);

  useEffect(
    (): (() => void) => (): void => {
      if (isModalOpen) {
        reset({ shareWithUserId: '', privateMessage: '' });
      }
    },
    [isModalOpen, reset]
  );

  useEffect((): void => {
    if (ticketId) {
      setSelectedTicketsIds([ticketId]);
    }
  }, [setSelectedTicketsIds, ticketId]);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const muiOptions = useMemo(
    (): MuiFormSelectOptionT[] =>
      agentIdsOptions.map(
        (o): MuiFormSelectOptionT => ({
          value: o.value,
          label: o.label,
          icon: (
            <Tooltip title={o.label || 'Unknown User'} arrow>
              <Avatar
                sx={{
                  ...AVATAR_STYLES.size,
                  fontSize: AVATAR_STYLES.fontSize,
                  backgroundColor: getConsistentAvatarBackgroundColor(String(o.value), isDarkMode),
                }}
              >
                {getInitials(o.label || 'Unknown User')}
              </Avatar>
            </Tooltip>
          ),
        })
      ),
    [agentIdsOptions, isDarkMode]
  );

  const showShareTicketForm = selectedTicketsIds.length > 0 || selectedTicketId != null;

  return (
    <Popup
      showCloseBtn
      modalTitle="Share"
      size={ModalSizeE.REASSIGN}
      popupId={ScrollLockIdE.OpenNewTicketPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isModalOpen}
      onClose={handleCloseShareTicketPopup(onClose)}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      data-testid="share-popup"
      validationErrorCount={Object.keys(formState.errors).length}
    >
      {showShareTicketForm && (
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ width: '100%' }}>
          <FormProvider {...fromMethods}>
            <Box
              component="form"
              onSubmit={handleSubmit(data => handleSubmitForm(data, onClose))}
              sx={{
                width: isMobile ? '100%' : 300,
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: isMobile ? undefined : 200,
              }}
            >
              <Box sx={{ mb: 2 }}>
                <MuiFormSelect
                  name="shareWithUserId"
                  label="Agent"
                  placeholder=""
                  options={muiOptions}
                  isLoading={isLoadingAgentOptions}
                  required
                  onChange={(v): void => handleAgentChange(String(v))}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Controller
                  name="privateMessage"
                  control={fromMethods.control}
                  render={({ field, fieldState }): JSX.Element => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Private Message"
                      placeholder="Message"
                      multiline
                      rows={3}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      value={field.value ?? ''}
                    />
                  )}
                />
              </Box>
              <Box display="flex" justifyContent={isMobile ? 'center' : 'flex-end'}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSharingTicket}
                  sx={{ flex: isMobile ? 1 : undefined }}
                >
                  {isSharingTicket ? 'Sharing...' : 'Share'}
                </Button>
              </Box>
            </Box>
          </FormProvider>
        </Box>
      )}

      {!selectedTicketsIds.length && selectedTicketId == null && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ px: { xs: 0, sm: 2.5 }, pb: 0.625 }}
        >
          <DangerousIcon size="13xl" color="red" />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Please select at least one ticket
          </Typography>
        </Box>
      )}
    </Popup>
  );
};

export default ShareTicketPopup;
